/** Sustained frame processing cost is a load proxy, not a temperature reading. */
const CEILINGS = [20, 16, 12, 8, 6] as const;
const IDLE_FPS = 3;
const QUIET_MS = 600;
// Leave time for recognition, the native preview and interaction. These are conservative
// policy targets, not measured thermal limits; validate them on the target phones.
const BUSY_MS_PER_SECOND = 450;
const RECOVERY_MS_PER_SECOND = 300;
const SAMPLE_COUNT = 12;
const LOWER_AFTER_MS = 2500;
const RECOVER_AFTER_MS = 12000;

type Scene = "active" | "empty" | "latched";

/** Bounded-rate detector starts, with cheap frame-content evidence between them. */
export class DetectorCadence {
  private scene: Scene = "active";
  private quietSince: number | null = null;
  private awakeUntil = 0;
  private ceilingIndex = 0;
  private nextAt = -Infinity;
  private lastStartedAt = -Infinity;
  private scheduledFps = CEILINGS[0] as number;
  private samples = new Float32Array(SAMPLE_COUNT);
  private sampleIndex = 0;
  private sampleCount = 0;
  private sampleTotal = 0;
  private lastSampleAt: number | null = null;
  private adjustedAt: number | null = null;
  private overloadedSince: number | null = null;
  private recoverySince: number | null = null;

  /** A pause/reframe invalidates quiet evidence, but does not erase a reduced ceiling. */
  resetEvidence(now: number): void {
    this.scene = "active";
    this.quietSince = null;
    this.awakeUntil = now + QUIET_MS;
    this.recoverySince = null;
    this.overloadedSince = null;
  }

  observeScene(scene: Scene, now: number): void {
    if (scene === "active") this.quietSince = null;
    else if (scene !== this.scene || this.quietSince === null) this.quietSince = now;
    this.scene = scene;
  }

  /** Null means the probe cannot vouch for quiet; fail open to the active ceiling. */
  observeContent(changed: boolean | null, now: number): void {
    if (changed !== false) {
      this.awakeUntil = now + QUIET_MS;
      if (this.quietSince !== null) this.quietSince = now;
    }
  }

  get ceilingFps(): number {
    return CEILINGS[this.ceilingIndex];
  }

  get rollingFrameMs(): number | null {
    return this.sampleCount ? this.sampleTotal / this.sampleCount : null;
  }

  targetFps(now: number): number {
    const quiet =
      this.scene !== "active" &&
      this.quietSince !== null &&
      now - this.quietSince >= QUIET_MS &&
      now >= this.awakeUntil;
    return quiet ? Math.min(IDLE_FPS, this.ceilingFps) : this.ceilingFps;
  }

  /**
   * Advance a phase deadline so 20 fps can select two of three 30 fps camera frames.
   * A late inference resets the phase; no missed detector starts are ever queued.
   * Waking from idle shortens the deadline only to the current performance ceiling.
   */
  shouldStart(now: number): boolean {
    const fps = this.targetFps(now);
    const interval = 1000 / fps;
    if (fps !== this.scheduledFps) {
      this.nextAt = this.lastStartedAt + interval;
      this.scheduledFps = fps;
    }
    if (now + 0.1 < this.nextAt) return false;
    this.nextAt =
      Number.isFinite(this.nextAt) && now - this.nextAt < interval
        ? this.nextAt + interval
        : now + interval;
    this.lastStartedAt = now;
    return true;
  }

  /**
   * Include sampling, packing, inference and decoding. Subtract only measured ORT
   * queue waiting, never the time spent doing real work while recognition is active.
   */
  observeFrame(now: number, milliseconds: number, queueMs = 0): void {
    milliseconds -= queueMs;
    if (!Number.isFinite(milliseconds) || milliseconds <= 0 || queueMs < 0) return;
    if (this.lastSampleAt !== null && now - this.lastSampleAt > 3000) {
      this.samples.fill(0);
      this.sampleCount = this.sampleIndex = this.sampleTotal = 0;
      this.recoverySince = null;
      this.overloadedSince = null;
    }
    this.lastSampleAt = now;
    this.adjustedAt ??= now;
    this.sampleTotal -= this.samples[this.sampleIndex];
    this.samples[this.sampleIndex] = milliseconds;
    this.sampleTotal += milliseconds;
    this.sampleIndex = (this.sampleIndex + 1) % SAMPLE_COUNT;
    this.sampleCount = Math.min(SAMPLE_COUNT, this.sampleCount + 1);
    if (this.sampleCount < 6) return;

    const mean = this.sampleTotal / this.sampleCount;
    if (mean * this.ceilingFps > BUSY_MS_PER_SECOND) {
      this.recoverySince = null;
      this.overloadedSince ??= now;
      if (
        this.ceilingIndex < CEILINGS.length - 1 &&
        now - this.adjustedAt >= LOWER_AFTER_MS &&
        now - this.overloadedSince >= LOWER_AFTER_MS
      ) {
        this.ceilingIndex++;
        this.adjustedAt = now;
        this.overloadedSince = now;
      }
      return;
    }
    this.overloadedSince = null;
    const higher = CEILINGS[this.ceilingIndex - 1];
    if (higher !== undefined && mean * higher < RECOVERY_MS_PER_SECOND) {
      this.recoverySince ??= now;
      if (now - this.recoverySince >= RECOVER_AFTER_MS) {
        this.ceilingIndex--;
        this.adjustedAt = now;
        this.recoverySince = null;
      }
    } else {
      this.recoverySince = null;
    }
  }
}
